// Centralized Froala + MathType setup to guarantee load order before React renders editors.
// CSS first so icons/fonts load
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';
import '@wiris/mathtype-froala/icons/css/wirisplugin.css';
import '@wiris/mathtype-html-integration-devkit/styles/styles.css';

// Core JS (pkgd includes all Froala plugins)
import FroalaEditor from 'froala-editor';
import 'froala-editor/js/plugins.pkgd.min.js';

// Expose global for Wiris
if (typeof window !== 'undefined') {
  window.FroalaEditor = window.FroalaEditor || FroalaEditor;
  // Some integrations resolve the global identifier instead of window property.
  // globalThis works in browsers and avoids ReferenceError in strict contexts.
  globalThis.FroalaEditor = globalThis.FroalaEditor || window.FroalaEditor;
}

// Ensure WIRIS always has a Service Provider URI available.
// The WIRIS Froala package defaults to `process.env.SERVICE_PROVIDER_URI`, which is
// not provided by CRA builds, and that leads to `undefined.indexOf(...)` crashes.
const defaultWirisServiceProviderUri =
  process.env.REACT_APP_WIRIS_SERVICE_PROVIDER_URI || 'https://www.wiris.net/demo/plugins/app';

// Runtime diagnostics to quickly identify which WIRIS endpoints are being used.
// Useful when icons/assets fail to load due to upstream outages (503), mixed content, or CSP.
const installWirisDiagnostics = () => {
  if (typeof window === 'undefined') return;
  if (window.__otsWirisDiagnosticsInstalled) return;
  window.__otsWirisDiagnosticsInstalled = true;

  const extractUrls = (text) => {
    if (typeof text !== 'string') return [];
    const matches = text.match(/https?:\/\/[^\s"')<>]+/gi);
    if (!matches) return [];
    // De-dupe and keep small.
    return Array.from(new Set(matches)).slice(0, 25);
  };

  const pickWirisUrls = (urls) => urls.filter((u) => /\bwiris\.(net|com)\b/i.test(u));

  window.__otsPrintWirisConfig = () => {
    try {
      window.__otsWirisDebug = window.__otsWirisDebug || {};

      const Configuration = window.WirisPlugin?.Configuration;
      const editorUrl = Configuration?.get && typeof Configuration.get === 'function' ? Configuration.get('editorUrl') : undefined;
      const parseModes = Configuration?.get && typeof Configuration.get === 'function' ? Configuration.get('parseModes') : undefined;

      const froalaDefaults = window.FroalaEditor?.DEFAULTS?.mathTypeParameters;
      const spUri = froalaDefaults?.serviceProviderProperties?.URI;

      const raw = window.__otsWirisDebug.lastConfigurationJsRaw;
      const rawUrls = extractUrls(raw);
      const wirisUrls = pickWirisUrls(rawUrls);

      // Surface the most useful values in console and keep a snapshot on window.
      const snapshot = {
        page: window.location?.href,
        protocol: window.location?.protocol,
        defaultServiceProviderUri: defaultWirisServiceProviderUri,
        froalaMathTypeServiceProviderUri: spUri,
        wirisConfigurationEditorUrl: editorUrl,
        wirisConfigurationParseModes: parseModes,
        configurationJsWirisUrls: wirisUrls,
        configurationJsAnyUrls: rawUrls,
      };

      window.__otsWirisDebug.configSnapshot = snapshot;

      // Log in a readable way.
      // eslint-disable-next-line no-console
      console.groupCollapsed('[OTS][WIRIS] Config snapshot');
      // eslint-disable-next-line no-console
      console.table(snapshot);
      if (wirisUrls.length) {
        // eslint-disable-next-line no-console
        console.log('[OTS][WIRIS] URLs in configurationjs (wiris.*):', wirisUrls);
      }
      // eslint-disable-next-line no-console
      console.groupEnd();

      return snapshot;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[OTS][WIRIS] Failed to print config snapshot', e);
      return null;
    }
  };

  // Convenience: only auto-log once per page load.
  window.__otsTryAutoPrintWirisConfigOnce = () => {
    try {
      window.__otsWirisDebug = window.__otsWirisDebug || {};
      if (window.__otsWirisDebug.__printedConfigSnapshot) return;
      window.__otsWirisDebug.__printedConfigSnapshot = true;
      window.__otsPrintWirisConfig?.();
    } catch {
      // ignore
    }
  };

  // Log failing WIRIS network calls (helps diagnose missing icon fonts/assets).
  // This is intentionally lightweight and only logs when status is >= 400.
  try {
    const isWirisUrl = (url) => {
      if (typeof url !== 'string') return false;
      return /\bwiris\.(net|com)\b/i.test(url);
    };

    if (typeof window.fetch === 'function' && !window.fetch.__otsWirisWrapped) {
      const originalFetch = window.fetch.bind(window);
      const wrapped = async (...args) => {
        const input = args[0];
        const url = typeof input === 'string' ? input : input?.url;
        try {
          const res = await originalFetch(...args);
          if (isWirisUrl(url) && res && typeof res.status === 'number' && res.status >= 400) {
            // eslint-disable-next-line no-console
            console.warn('[OTS][WIRIS] fetch failed', res.status, url);
          }
          return res;
        } catch (e) {
          if (isWirisUrl(url)) {
            // eslint-disable-next-line no-console
            console.warn('[OTS][WIRIS] fetch threw', url, e);
          }
          throw e;
        }
      };
      wrapped.__otsWirisWrapped = true;
      wrapped.__otsOriginal = originalFetch;
      window.fetch = wrapped;
    }

    if (window.XMLHttpRequest && !window.XMLHttpRequest.__otsWirisWrapped) {
      const XHR = window.XMLHttpRequest;
      const origOpen = XHR.prototype.open;
      const origSend = XHR.prototype.send;

      XHR.prototype.open = function (method, url, ...rest) {
        try {
          this.__otsWirisUrl = typeof url === 'string' ? url : undefined;
        } catch {
          // ignore
        }
        return origOpen.call(this, method, url, ...rest);
      };

      XHR.prototype.send = function (...sendArgs) {
        try {
          const url = this.__otsWirisUrl;
          if (isWirisUrl(url) && !this.__otsWirisListenerAttached) {
            this.__otsWirisListenerAttached = true;
            this.addEventListener('loadend', function () {
              try {
                const status = this.status;
                if (typeof status === 'number' && status >= 400) {
                  // eslint-disable-next-line no-console
                  console.warn('[OTS][WIRIS] xhr failed', status, url);
                }
              } catch {
                // ignore
              }
            });
          }
        } catch {
          // ignore
        }
        try {
          return origSend.apply(this, sendArgs);
        } catch (err) {
          // Catch NetworkError thrown by send() when CORS blocks the request
          // (e.g. WIRIS demo endpoint returning 403 for production origins).
          // eslint-disable-next-line no-console
          console.warn('[OTS][WIRIS] xhr send() blocked (CORS/network)', this.__otsWirisUrl, err?.message);
          return undefined;
        }
      };

      window.XMLHttpRequest.__otsWirisWrapped = true;
    }
  } catch {
    // ignore
  }
};

// Some legacy stylesheets in this project historically applied a broad rule like:
//   [class^="icon-"], [class*=" icon-"] { font-family: 'icomoon' !important; }
// WIRIS uses `icon-mathtype-*` classes, so that rule can silently blank WIRIS icons.
// Force the correct font for WIRIS icon classes at runtime, after all CSS has loaded.
const injectWirisIconFontOverride = () => {
  if (typeof document === 'undefined') return;
  const styleId = 'ots-wiris-icon-font-override';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.type = 'text/css';
  style.textContent = `
/* Fallback visible icons for MathType/ChemType toolbar buttons.
   The WIRIS icon font ("wirisplugin") often fails to load in CRA/webpack builds
   because the font-file paths are not resolved by the bundler. We override the
   ::before content with standard Unicode symbols so the buttons are always visible. */
.fr-toolbar button.fr-command[data-cmd="wirisEditor"] i.icon::before,
i.icon.icon-mathtype-editor::before {
  content: "\\03A3" !important;          /* Greek capital Sigma: Σ */
  font-family: "Times New Roman", Georgia, serif !important;
  font-size: 20px !important;
  font-style: italic !important;
  font-weight: normal !important;
  line-height: 1 !important;
  speak: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.fr-toolbar button.fr-command[data-cmd="wirisChemistry"] i.icon::before,
i.icon.icon-mathtype-chemistry::before {
  content: "\\2697" !important;          /* Alembic: ⚗ */
  font-family: "Times New Roman", Georgia, serif !important;
  font-size: 17px !important;
  font-style: normal !important;
  font-weight: normal !important;
  line-height: 1 !important;
  speak: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}


/* Ensure WIRIS MathType palette icons (img tags loaded from the WIRIS service)
   are fully visible. Bootstrap/AdminLTE can sometimes interfere with these. */
.wrs_panelContainer img,
.wrs_toolbar img,
.wrs_modal_desktop img,
.wrs_modal_dialogContainer img {
  opacity: 1 !important;
  visibility: visible !important;
  display: inline-block !important;
  max-width: none !important;
  filter: none !important;
}

/* The WIRIS editor palette is built by the remotely-loaded editor script.
   It uses <td> elements with background-image for symbol icons.
   AdminLTE / Bootstrap can strip backgrounds or force box-sizing that
   collapses cells to zero size.  Override broadly for anything inside
   the MathType modal dialog. */
.wrs_modal_dialogContainer td,
.wrs_modal_dialogContainer td > *,
.wrs_modal_dialogContainer table {
  background-color: transparent;
  background-image: revert !important;
  box-sizing: content-box !important;
}
.wrs_modal_dialogContainer td img,
.wrs_modal_dialogContainer td canvas,
.wrs_modal_dialogContainer td svg {
  opacity: 1 !important;
  visibility: visible !important;
  max-width: none !important;
  max-height: none !important;
  width: auto !important;
  height: auto !important;
  filter: none !important;
  display: inline !important;
}
/* Force all palette sub-elements to be visible */
.wrs_modal_dialogContainer * {
  color: inherit;
}
/* Undo any global "a { background: 0 0 !important }" from AdminLTE */
.wrs_modal_dialogContainer a,
.wrs_modal_dialogContainer button {
  background: revert !important;
  background-image: revert !important;
}
`;
  document.head.appendChild(style);
};

// Some admin theme CSS resets can wipe background images on <a>/<button> with `!important`.
// WIRIS MathType modal control icons (close/min/max/stack) are inline SVG background-images.
// If a global rule forces `background(-image): none !important`, those icons go blank.
// We override that *only* for WIRIS modal controls and allow the inline style to win.
const injectWirisModalBackgroundRestore = () => {
  if (typeof document === 'undefined') return;
  const styleId = 'ots-wiris-modal-bg-restore';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.type = 'text/css';
  style.textContent = `
.wrs_modal_close_button,
.wrs_modal_minimize_button,
.wrs_modal_maximize_button,
.wrs_modal_stack_button {
  /* Beat broad theme rules like: a { background: 0 0 !important; } */
  background: revert !important;
  background-image: revert !important;
}
`;
  document.head.appendChild(style);
};

// Extra safety: if some stylesheet still overrides inline `background-image` via `!important`,
// force the inline value to `!important` once the modal appears.
const installWirisModalIconObserver = () => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
  if (window.__otsWirisModalObserverInstalled) return;
  window.__otsWirisModalObserverInstalled = true;

  const fixElement = (el) => {
    try {
      if (!el || !el.style) return;
      const inlineBg = el.style.backgroundImage;
      if (!inlineBg) return;
      const computedBg = window.getComputedStyle(el)?.backgroundImage;
      if (computedBg === 'none' || computedBg === '' || computedBg == null) {
        el.style.setProperty('background-image', inlineBg, 'important');
      }
      const inlineSize = el.style.backgroundSize;
      if (inlineSize) el.style.setProperty('background-size', inlineSize, 'important');
      const inlinePos = el.style.backgroundPosition;
      if (inlinePos) el.style.setProperty('background-position', inlinePos, 'important');
      const inlineRepeat = el.style.backgroundRepeat;
      if (inlineRepeat) el.style.setProperty('background-repeat', inlineRepeat, 'important');
    } catch {
      // ignore
    }
  };

  const selector =
    '.wrs_modal_close_button, .wrs_modal_minimize_button, .wrs_modal_maximize_button, .wrs_modal_stack_button';

  const scan = () => {
    try {
      document.querySelectorAll(selector).forEach(fixElement);
    } catch {
      // ignore
    }
  };

  // Scan now and whenever WIRIS inserts the modal into DOM.
  scan();
  const observer = new MutationObserver(() => scan());
  observer.observe(document.documentElement, { subtree: true, childList: true });
};

if (typeof window !== 'undefined') {
  try {
    installWirisDiagnostics();
    injectWirisIconFontOverride();
    injectWirisModalBackgroundRestore();
    installWirisModalIconObserver();
  } catch {
    // ignore
  }
}

// Detect whether we should skip WIRIS network calls entirely.
// The wiris.net/demo endpoint serves both demo and licensed accounts — licensed origins
// (onetimesuccess.com, staging.onetimesuccess.com) are whitelisted on WIRIS's backend.
// Only skip init for unlicensed/unknown origins to avoid 403/CORS errors.
const isWirisDemoUri = /\/\/www\.wiris\.net\/demo\//i.test(defaultWirisServiceProviderUri);
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');
const isOtsOrigin =
  typeof window !== 'undefined' &&
  /^(www\.)?(staging\.)?onetimesuccess\.com$/i.test(window.location?.hostname || '');
const skipWirisInit = isWirisDemoUri && !isLocalhost && !isOtsOrigin;

if (skipWirisInit) {
  // eslint-disable-next-line no-console
  console.warn(
    '[OTS][WIRIS] Skipping WIRIS network initialization — the demo service at wiris.net/demo '
    + 'does not allow requests from this origin. Set REACT_APP_WIRIS_SERVICE_PROVIDER_URI to a '
    + 'licensed/self-hosted WIRIS endpoint to enable MathType in production.'
  );
}

// Attach MathType/WIRIS plugin (registers commands/icons)
// NOTE: Do NOT use static import here; ES module imports are hoisted and will run
// before we assign window.FroalaEditor, causing "FroalaEditor is not defined".
if (typeof window !== 'undefined' && window.FroalaEditor && !skipWirisInit) {
  // react-froala-wysiwyg may use the module export instance. Ensure we patch BOTH.
  // eslint-disable-next-line global-require
  const froalaModule = require('froala-editor');

  const froalaTargets = Array.from(
    new Set([window.FroalaEditor, globalThis.FroalaEditor, froalaModule].filter(Boolean))
  );

  // Provide global defaults so *any* Froala editor instance has valid MathType params.
  froalaTargets.forEach((FE) => {
    try {
      if (!FE.DEFAULTS) FE.DEFAULTS = {};
      const current = FE.DEFAULTS.mathTypeParameters && typeof FE.DEFAULTS.mathTypeParameters === 'object'
        ? FE.DEFAULTS.mathTypeParameters
        : {};
      const sp = current.serviceProviderProperties && typeof current.serviceProviderProperties === 'object'
        ? current.serviceProviderProperties
        : {};

      FE.DEFAULTS.mathTypeParameters = {
        editorEnabled: typeof current.editorEnabled === 'boolean' ? current.editorEnabled : true,
        chemEnabled: typeof current.chemEnabled === 'boolean' ? current.chemEnabled : true,
        ...current,
        serviceProviderProperties: {
          ...sp,
          URI: sp.URI || defaultWirisServiceProviderUri,
        },
      };
    } catch {
      // ignore
    }
  });

  // eslint-disable-next-line global-require
  const attachWiris = require('@wiris/mathtype-froala');
  if (typeof attachWiris === 'function') {
    froalaTargets.forEach((target) => {
      try {
        attachWiris(target);
      } catch {
        // ignore
      }
    });
  }

  // Patch WIRIS ServiceProvider at runtime (no `@wiris/.../src/*` imports).
  // Only force HTTPS when the host page is HTTPS.
  // The WIRIS demo service returns an `http://` editorUrl by default, which is fine on
  // `http://localhost` but becomes mixed-content when your site is served over HTTPS.
  try {
    const ServiceProvider = window.WirisPlugin?.ServiceProvider;
    if (
      ServiceProvider &&
      typeof ServiceProvider.getService === 'function' &&
      !ServiceProvider.__otsPatchedForceHttpsConfiguration
    ) {
      const originalGetService = ServiceProvider.getService;

      const rewriteWirisUrlsToHttps = (text) => {
        if (typeof text !== 'string' || text.length === 0) return text;
        if (window.location?.protocol !== 'https:') return text;
        return text
          .replace(/http:\/\/www\.wiris\.net/gi, 'https://www.wiris.net')
          .replace(/http:\/\/www\.wiris\.com/gi, 'https://www.wiris.com')
          .replace(/http:\/\/(?!localhost)([^\s'"\\)]*\bwiris\.(?:net|com)\b)/gi, 'https://$1');
      };

      ServiceProvider.getService = function (service, postVariables, get) {
        const response = originalGetService.call(this, service, postVariables, get);
        if (service === 'configurationjs') {
          try {
            window.__otsWirisDebug = window.__otsWirisDebug || {};
            window.__otsWirisDebug.lastConfigurationJsRaw =
              typeof response === 'string' ? response.slice(0, 20000) : response;
            if (typeof response === 'string') {
              try {
                window.__otsWirisDebug.lastConfigurationObj = JSON.parse(response);
              } catch {
                // ignore (not always strict JSON)
              }
            }
          } catch {
            // ignore
          }

          try {
            window.__otsTryAutoPrintWirisConfigOnce?.();
          } catch {
            // ignore
          }

          return rewriteWirisUrlsToHttps(response);
        }
        return response;
      };

      ServiceProvider.__otsPatchedForceHttpsConfiguration = true;
    }
  } catch {
    // ignore
  }

  // Patch WIRIS integration to tolerate empty editor HTML during startup.
  // The upstream plugin assumes html.get() always returns a string.
  try {
    // Force HTTPS for editor assets.
    // Only do this when the host page is HTTPS (to avoid mixed content).
    try {
      const Configuration = window.WirisPlugin?.Configuration;
      if (
        Configuration &&
        typeof Configuration.addConfiguration === 'function' &&
        typeof Configuration.get === 'function' &&
        typeof Configuration.set === 'function' &&
        !Configuration.__otsPatchedForceHttpsEditorUrl
      ) {
        const originalAddConfiguration = Configuration.addConfiguration;

        const coerceHttpsWirisUrl = (url) => {
          if (typeof url !== 'string') return url;
          if (window.location?.protocol !== 'https:') return url;
          // Prefer https for any absolute http url.
          if (url.startsWith('http://')) return `https://${url.slice('http://'.length)}`;
          return url;
        };

        const deepCoerceHttpsWirisUrls = (value, seen = new Set()) => {
          if (value == null) return value;
          if (typeof value === 'string') {
            if (window.location?.protocol !== 'https:') return value;
            // Only coerce WIRIS domains to avoid touching unrelated urls.
            if (/^http:\/\/[^\s'"\\)]*\bwiris\.(net|com)\b/i.test(value)) {
              return value.replace(/^http:\/\//i, 'https://');
            }
            return value;
          }
          if (typeof value !== 'object') return value;
          if (seen.has(value)) return value;
          seen.add(value);

          if (Array.isArray(value)) {
            return value.map((v) => deepCoerceHttpsWirisUrls(v, seen));
          }

          const next = { ...value };
          Object.keys(next).forEach((k) => {
            next[k] = deepCoerceHttpsWirisUrls(next[k], seen);
          });
          return next;
        };

        Configuration.addConfiguration = function (properties) {
          let nextProps = properties;
          try {
            if (properties && typeof properties === 'object') {
              const coerced = deepCoerceHttpsWirisUrls(properties);
              const editorUrl = coerced.editorUrl;
              const httpsEditorUrl = coerceHttpsWirisUrl(editorUrl);
              if (httpsEditorUrl !== editorUrl) {
                nextProps = { ...coerced, editorUrl: httpsEditorUrl };
              } else {
                nextProps = coerced;
              }

              try {
                window.__otsWirisDebug = window.__otsWirisDebug || {};
                window.__otsWirisDebug.lastAddConfigurationProps = { ...properties };
              } catch {
                // ignore
              }
            }
          } catch {
            // ignore
          }

          const result = originalAddConfiguration.call(this, nextProps);

          try {
            const currentEditorUrl = Configuration.get('editorUrl');
            const httpsEditorUrl = coerceHttpsWirisUrl(currentEditorUrl);
            if (httpsEditorUrl && httpsEditorUrl !== currentEditorUrl) {
              Configuration.set('editorUrl', httpsEditorUrl);
            }

            try {
              window.__otsWirisDebug = window.__otsWirisDebug || {};
              window.__otsWirisDebug.currentEditorUrl = Configuration.get('editorUrl');
            } catch {
              // ignore
            }
          } catch {
            // ignore
          }

          return result;
        };

        Configuration.__otsPatchedForceHttpsEditorUrl = true;
      }
    } catch {
      // ignore
    }

    // Hard safety: ensure devkit IntegrationModel never sees undefined serviceProviderProperties.
    // If URI/server are missing, minified wiris.js will crash on `.indexOf(...)`.
    try {
      const IntegrationModel = window.WirisPlugin?.IntegrationModel;
      const imProto = IntegrationModel?.prototype;
      if (imProto && typeof imProto.init === 'function' && !imProto.__otsPatchedSafeServiceProvider) {
        const originalInit = imProto.init;
        imProto.init = function (...args) {
          try {
            if (!this.serviceProviderProperties || typeof this.serviceProviderProperties !== 'object') {
              this.serviceProviderProperties = {};
            }

            const uri = this.serviceProviderProperties.URI;
            if (typeof uri !== 'string' || uri.length === 0) {
              this.serviceProviderProperties.URI = defaultWirisServiceProviderUri;
            }

            const server = this.serviceProviderProperties.server;
            if (typeof server !== 'string' || server.length === 0) {
              // devkit expects a string and calls `server.indexOf(...)`.
              this.serviceProviderProperties.server = 'java';
            }
          } catch {
            // ignore
          }
          return originalInit.apply(this, args);
        };
        imProto.__otsPatchedSafeServiceProvider = true;
      }
    } catch {
      // ignore
    }

    const FroalaIntegration = window.WirisPlugin?.FroalaIntegration;
    const proto = FroalaIntegration?.prototype;
    if (proto && typeof proto.parseMathMLProperties === 'function' && !proto.__otsPatchedParseMathMLProperties) {
      const originalParse = proto.parseMathMLProperties;
      proto.parseMathMLProperties = function (html) {
        return originalParse.call(this, html ?? '');
      };
      proto.__otsPatchedParseMathMLProperties = true;
    }

    // Critical patch: WIRIS registers an html.get handler that may receive non-string
    // args depending on Froala version/build. When it returns non-string/undefined,
    // Froala undo snapshots can end up with null html and crash on `split(null)`.
    // We intercept the registration inside WIRIS init and make it signature-tolerant.
    if (proto && typeof proto.init === 'function' && !proto.__otsPatchedWirisHtmlGetInit) {
      const originalInit = proto.init;
      proto.init = function (...initArgs) {
        // Ensure WIRIS configuration has sane defaults before any Parser logic runs.
        try {
          const Configuration = window.WirisPlugin?.Configuration;
          if (Configuration && typeof Configuration.get === 'function' && typeof Configuration.set === 'function') {
            const parseModes = Configuration.get('parseModes');
            // WIRIS Configuration.get returns `false` when a key is missing.
            // Parser expects an array and calls `.indexOf(...)` on it.
            if (!Array.isArray(parseModes)) {
              Configuration.set('parseModes', []);
            }
          }
        } catch {
          // ignore
        }

        // Instance-level safety net: `parseMathMLProperties` must never receive undefined.
        // Some WIRIS/Froala codepaths call it early during init.
        try {
          if (!this.__otsPatchedInstanceParseMathMLProperties && typeof this.parseMathMLProperties === 'function') {
            const originalParse = this.parseMathMLProperties;
            this.parseMathMLProperties = function (text) {
              const safeText = typeof text === 'string' ? text : text == null ? '' : String(text);
              return originalParse.call(this, safeText);
            };
            this.__otsPatchedInstanceParseMathMLProperties = true;
          }
        } catch {
          // ignore
        }

        const editor = this?.editorObject;

        if (editor?.events?.on) {
          const originalOn = editor.events.on;

          editor.events.on = function (eventName, callback, ...rest) {
            // Defensive: Froala's internal events manager assumes `eventName` is a string
            // and may call `eventName.indexOf(...)`. If any plugin calls events.on with
            // undefined / non-string during WIRIS init, skip it to avoid hard crashes.
            if (typeof eventName !== 'string' || eventName.length === 0) {
              return undefined;
            }

            if (eventName === 'html.get' && typeof callback === 'function') {
              const wrapped = function (...cbArgs) {
                const htmlArg = cbArgs.find((a) => typeof a === 'string') ?? '';
                try {
                  const Parser = window.WirisPlugin?.Parser;
                  if (typeof Parser?.endParse === 'function') {
                    return Parser.endParse(htmlArg);
                  }
                  return htmlArg;
                } catch {
                  return htmlArg;
                }
              };
              return originalOn.call(this, eventName, wrapped, ...rest);
            }
            return originalOn.call(this, eventName, callback, ...rest);
          };

          try {
            return originalInit.apply(this, initArgs);
          } finally {
            editor.events.on = originalOn;
          }
        }

        return originalInit.apply(this, initArgs);
      };
      proto.__otsPatchedWirisHtmlGetInit = true;
    }
  } catch {
    // Ignore patch failures.
  }

  // Patch WIRIS Parser helpers too; IntegrationModel.callbackFunction calls Parser.initParse(html)
  // during initialization and html can be nullish before the React wrapper sets initial content.
  try {
    const Parser = window.WirisPlugin?.Parser;
    if (Parser && typeof Parser.initParse === 'function' && !Parser.__otsPatchedInitParse) {
      const originalInitParse = Parser.initParse;
      Parser.initParse = function (html, ...rest) {
        return originalInitParse.call(this, html ?? '', ...rest);
      };
      Parser.__otsPatchedInitParse = true;
    }

    if (Parser && typeof Parser.endParse === 'function' && !Parser.__otsPatchedEndParse) {
      const originalEndParse = Parser.endParse;
      Parser.endParse = function (html, ...rest) {
        return originalEndParse.call(this, html ?? '', ...rest);
      };
      Parser.__otsPatchedEndParse = true;
    }

    // Guard against Configuration.get('parseModes') returning false/undefined.
    // The devkit uses `.indexOf('latex')` on it.
    if (Parser && typeof Parser.endParseEditMode === 'function' && !Parser.__otsPatchedEndParseEditMode) {
      const originalEndParseEditMode = Parser.endParseEditMode;
      Parser.endParseEditMode = function (code, ...rest) {
        try {
          const Configuration = window.WirisPlugin?.Configuration;
          if (Configuration && typeof Configuration.get === 'function' && typeof Configuration.set === 'function') {
            const parseModes = Configuration.get('parseModes');
            if (!Array.isArray(parseModes) && typeof parseModes !== 'string') {
              Configuration.set('parseModes', []);
            }
          }
        } catch {
          // ignore
        }
        return originalEndParseEditMode.call(this, code ?? '', ...rest);
      };
      Parser.__otsPatchedEndParseEditMode = true;
    }
  } catch {
    // Ignore patch failures.
  }

  // Defensive patch: prevent WIRIS refresh/callback crashes when Froala's DOM wrapper
  // returns unexpected values (e.g. getAttribute() returning null/undefined).
  try {
    const WirisCfg = window.WirisPlugin?.Configuration;

    const safeGetParentClass = ($btn) => {
      try {
        const parentEl = $btn?.parent?.()?.[0];
        if (!parentEl || typeof parentEl.getAttribute !== 'function') return '';
        return parentEl.getAttribute('class') || '';
      } catch {
        return '';
      }
    };

    const wrapCommandCallback = (FE, commandName) => {
      const cmd = FE?.COMMANDS?.[commandName];
      if (!cmd || typeof cmd.callback !== 'function') return;
      const original = cmd.callback;
      cmd.callback = function (...args) {
        const instance = window.WirisPlugin?.instances?.[this.id];
        if (!instance) return;
        return original.apply(this, args);
      };
    };

    const patchRefresh = (FE, commandName) => {
      const cmd = FE?.COMMANDS?.[commandName];
      if (!cmd) return;
      cmd.refresh = function ($btn) {
        try {
          const selectedImage = this?.image?.get?.();
          if (!selectedImage) return;

          const parentClass = safeGetParentClass($btn);
          const notInToolbarButtons = parentClass ? !parentClass.includes('fr-buttons') : false;

          const imageClassName = WirisCfg?.get ? WirisCfg.get('imageClassName') : undefined;
          const image0 = selectedImage?.[0];
          const isMathTypeImage =
            !!imageClassName &&
            !!image0 &&
            (image0.classList?.contains?.(imageClassName) ||
              (typeof selectedImage.hasClass === 'function' && selectedImage.hasClass(imageClassName)));

          if (notInToolbarButtons || isMathTypeImage) {
            $btn?.removeClass?.('fr-hidden');
          } else {
            $btn?.addClass?.('fr-hidden');
          }
        } catch {
          // Never let refresh crash Froala/WIRIS.
        }
      };
    };

    froalaTargets.forEach((FE) => {
      wrapCommandCallback(FE, 'wirisEditor');
      wrapCommandCallback(FE, 'wirisChemistry');
      patchRefresh(FE, 'wirisEditor');
      patchRefresh(FE, 'wirisChemistry');
    });
  } catch {
    // Ignore patch failures.
  }
}

// WIRIS toolbar button icons are now handled purely via CSS injection
// (see injectWirisIconFontOverride above), so no DefineIcon overrides needed.
